/**
 * @file   src\components\SignTemplate01.tsx
 * @brief  SignTemplate component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import {
  React,
  useState,
  Button,
  useEffect,
  Row,
} from "../utils/thirdpartyComponents";
import "../styles/signtemplate.scss";
import QRCode from "qrcode.react";
import Scanarrow from "../assets/images/theme1-caret-up.svg";
import html2canvas from "html2canvas";
import { toPng, toJpeg } from "html-to-image";
import jsPDF from "jspdf";
import FullLoader from "../components/FullLoader";
import CWCheckbox from "./CWCheckbox";

const SignTemplate01 = (props: any) => {
  const [loading, setLoading] = useState(false);
  const [companyLogoBase64, setCompanyLogoBase64] = useState<string | null>(
    null
  );
  const [agreeTerms, setAgreeTerms] = useState<boolean>(false);
  // const convertImageToBase64 = async (url: string): Promise<string> => {
  //     return new Promise((resolve, reject) => {
  //         const img = new Image();
  //         img.crossOrigin = "anonymous";
  //         img.src = url;

  //         img.onload = () => {
  //             const canvas = document.createElement("canvas");
  //             canvas.width = img.width;
  //             canvas.height = img.height;
  //             const ctx = canvas.getContext("2d");
  //             if (ctx) {
  //                 ctx.drawImage(img, 0, 0);
  //                 resolve(canvas.toDataURL("image/png"));
  //             } else {
  //                 reject("Canvas context not available.");
  //             }
  //         };

  //         img.onerror = (err) => {
  //             reject(err);
  //         };
  //     });
  // };

  useEffect(() => {
    setCompanyLogoBase64(props.companyLogoUrl);
    // convertImageToBase64(props.companyLogoUrl)
    //     .then((base64) => {
    //         setCompanyLogoBase64(base64);
    //     })
    //     .catch((err) => {
    //         console.error("Failed to load image:", err);
    //     });
  }, [props.companyLogoUrl]);
  const captureId = "capture";

  // Download as PDF
  const downloadAsPDF = async () => {
    setLoading(true);
    const element = document.getElementById(captureId);
    if (!element) return;

    if (navigator.userAgent.toLowerCase().includes("firefox")) {
      try {
        const canvas = await html2canvas(element, {
          scale: 5, // Increases resolution (Higher value = Sharper output)
          useCORS: true, // Handles cross-origin images
          logging: false, // Reduces console noise
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");

        // Convert dimensions for A4 size
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save(`${props.washname}-template-1.pdf`);
        setLoading(false);
      } catch (error) {
        console.error("Error generating high-quality PDF:", error);
      }
    } else {
      try {
        // Generate high-quality PNG
        const dataUrl = await toPng(element, {
          quality: 1.0, // Ensures maximum quality
          canvasWidth: element.scrollWidth * 4, // Double resolution
          canvasHeight: element.scrollHeight * 4, // Double resolution
          pixelRatio: 4, // Increase pixel ratio for clarity
          cacheBust: true, // Prevent caching issues
        });

        // Initialize PDF
        const pdf = new jsPDF("p", "mm", "a4");

        // Convert dimensions
        const imgWidth = 210; // A4 width in mm
        const imgHeight =
          (pdf.internal.pageSize.getHeight() * imgWidth) /
          pdf.internal.pageSize.getWidth();

        // Add image to PDF
        pdf.addImage(dataUrl, "PNG", 0, 0, imgWidth, imgHeight, "", "FAST"); // "FAST" preserves clarity

        // Save the PDF
        pdf.save(`${props.washname}-template-1.pdf`);
        setLoading(false);
      } catch (error) {
        console.error("Error generating high-quality PDF:", error);
      }
    }
  };

  // Download as JPG
  const downloadAsJPG = async () => {
    setLoading(true);
    const element = document.getElementById(captureId);
    if (!element) return;

    if (navigator.userAgent.toLowerCase().includes("firefox")) {
      try {
        const canvas = await html2canvas(element, {
          scale: 5, // Increases resolution (Higher value = Sharper output)
          useCORS: true, // Handles cross-origin images
          logging: false, // Reduces console noise
        });

        const link = document.createElement("a");
        link.download = `${props.washname}-template-1.jpg`;
        link.href = canvas.toDataURL("image/jpeg", 1.0); // High-quality JPG
        link.click();
        setLoading(false);
      } catch (error) {
        console.error("Error generating high-quality JPG:", error);
      }
    } else {
      try {
        const dataUrl = await toJpeg(element, {
          quality: 1.0, // Ensures maximum JPEG quality
          canvasWidth: element.scrollWidth * 4, // Double resolution
          canvasHeight: element.scrollHeight * 4, // Double resolution
          pixelRatio: 4, // Increase pixel density for sharpness
          cacheBust: true, // Prevent caching issues
        });

        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${props.washname}-template-1.jpg`;
        link.click();
        setLoading(false);
      } catch (error) {
        console.error("Error generating high-quality JPG:", error);
      }
    }
  };

  // Download as PNG
  const downloadAsPNG = async () => {
    setLoading(true);
    const element = document.getElementById(captureId);
    if (!element) return;

    if (navigator.userAgent.toLowerCase().includes("firefox")) {
      try {
        const canvas = await html2canvas(element, {
          scale: 5, // Higher scale for better resolution
          useCORS: true, // Enables cross-origin image support
          logging: false, // Reduces console clutter
        });

        const link = document.createElement("a");
        link.download = `${props.washname}-template-1.png`;
        link.href = canvas.toDataURL("image/png"); // Lossless PNG format
        link.click();
        setLoading(false);
      } catch (error) {
        console.error("Error generating high-quality PNG:", error);
      }
    } else {
      try {
        const dataUrl = await toPng(element, {
          canvasWidth: element.scrollWidth * 4, // Increase resolution
          canvasHeight: element.scrollHeight * 4, // Increase resolution
          pixelRatio: 4, // Higher clarity
          cacheBust: true, // Prevent caching issues
        });

        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${props.washname}-template-1.png`;
        link.click();
        setLoading(false);
      } catch (error) {
        console.error("Error generating high-quality PNG:", error);
      }
    }
  };

  // // Function to download as EPS
  // const downloadAsEPS = async () => {
  //     setLoading(true);
  //     const element = document.getElementById(captureId);
  //     if (!element) return;

  //     try {
  //         const svgDataUrl = await toPng(element, {
  //           canvasWidth: element.scrollWidth * 4, // Increase resolution
  //           canvasHeight: element.scrollHeight * 4, // Increase resolution
  //           pixelRatio: 4, // Higher clarity
  //           cacheBust: true, // Prevent caching issues
  //         });

  //         const img = new Image();
  //         img.src = svgDataUrl;
  //         img.onload = () => {
  //           const canvas = document.createElement("canvas");
  //           canvas.width = img.width;
  //           canvas.height = img.height;
  //           const ctx = canvas.getContext("2d");

  //           if (ctx) {
  //             ctx.drawImage(img, 0, 0);
  //             const epsDataUrl = canvas.toDataURL("image/png"); // Converting to PNG first

  //             const link = document.createElement("a");
  //             link.href = epsDataUrl;
  //             link.download = `${props.washname} template-1.eps`;
  //             link.click();
  //             setLoading(false);
  //           }
  //         };
  //       } catch (error) {
  //         console.error("Error generating high-quality EPS:", error);
  //       }
  //   };

  return (
    <div>
      <div id={captureId} className="template-first">
        <div className="template-header text-center position-relative">
          <div className="header-bg"></div>
          <div className="logo d-flex align-items-center justify-content-center mx-auto">
            {companyLogoBase64 ? (
              <img src={companyLogoBase64} alt="Car Wash App" />
            ) : (
              <span>Loading logo...</span>
            )}
          </div>
          <h1
            className="f-extrabold"
            style={{ fontSize: `${props.washnamefontsize}` }}
          >
            {props.washname}
          </h1>
        </div>
        <div className="template-content text-center position-relative">
          <div className="bay-name f-bold mx-auto">{props.bayName}</div>
          <div className="qr-scanner mx-auto">
            <QRCode
              value={props.bayId}
              size={190}
              level={"H"}
              includeMargin={true}
              renderAs="svg"
              crossOrigin="anonymous"
            />
          </div>
          <div className="scan-text f-extrabold d-flex align-items-center flex-column justify-content-center">
            <img src={Scanarrow} alt="Car Wash App" />
            Scan in the App to Wash
          </div>
          <div className="main-title mx-auto">
            <h1 className="f-extrabold position-relative">
              {props.titlecontent}
            </h1>
          </div>
          <div className="website-info mx-auto d-flex align-items-center justify-content-between">
            <div className="app-url text-start">
              <h3 className="f-extrabold">Scan to get the app</h3>
              <p className="f-medium">APP URL: {props.appUrl}</p>
            </div>
            <div className="scanner d-flex align-items-center justify-content-end">
              <div className="overlay">
                <QRCode
                  value={props.appUrl}
                  size={85}
                  level={"H"}
                  includeMargin={true}
                  renderAs="svg"
                  crossOrigin="anonymous"
                />
              </div>
            </div>
          </div>
          <div className="plan-information d-flex justify-content-center">
            <div className="info-card d-flex align-items-center position-relative">
              {/* <span className="arrow"></span> */}
              <h3
                className="f-extrabold mx-auto"
                style={{ fontSize: `${props.box1fontsize}` }}
              >
                {props.subscriptionbox1}
              </h3>
            </div>
            <div className="info-card d-flex align-items-center position-relative">
              {/* <span className="arrow"></span> */}
              <h3
                className="f-extrabold mx-auto"
                style={{ fontSize: `${props.box2fontsize}` }}
              >
                {props.subscriptionbox2}
              </h3>
            </div>
            <div className="info-card d-flex align-items-center position-relative">
              {/* <span className="arrow"></span> */}
              <h3
                className="f-extrabold mx-auto"
                style={{ fontSize: `${props.box3fontsize}` }}
              >
                {props.subscriptionbox3}
              </h3>
            </div>
          </div>
        </div>
      </div>
      {/* <p className='note mt-2'>Note: Please proofread your sign before downloading. </p> */}
      <div className="mt-3">
        <CWCheckbox
          id="agree"
          name="agree"
          label="By checking this box, I (carwash owner) acknowledge that I have proofread the sign and checked all QR codes.  Any reprints will be the responsibility of the carwash owner."
          checked={agreeTerms}
          // disabled={props.edit ? true : false}
          onChange={(event: any) => setAgreeTerms(event.target.checked)}
        />
      </div>
      <div className="btn-sec d-flex mt-4">
        <Button
          title="Download as PDF"
          variant="primary"
          className="primary-btn download-btn"
          type="button"
          disabled={props.appUrl == "" || agreeTerms === false}
          onClick={downloadAsPDF}
        >
          Download as PDF
        </Button>
        <Button
          title="Download as JPG"
          variant="primary"
          className="primary-btn download-btn"
          type="button"
          disabled={props.appUrl == "" || agreeTerms === false}
          onClick={downloadAsJPG}
        >
          Download as JPG
        </Button>
        <Button
          title="Download as PNG"
          variant="primary"
          className="primary-btn download-btn"
          type="button"
          disabled={props.appUrl == "" || agreeTerms === false}
          onClick={downloadAsPNG}
        >
          Download as PNG
        </Button>

        {/* <Button
            title="Download as EPS"
            variant="primary"
            className="primary-btn download-btn"
            type="button"
            onClick={downloadAsEPS}
        >
            Download as EPS
        </Button> */}
      </div>
      <div className="mt-3">
        <span className="error-msg">
          App URL need to be generated for Downloading the Sign.
        </span>
      </div>
      <div className="mt-1">
        <span className="error-msg">
        For best results use Chrome browser.         
        </span>
      </div>
        

      {loading && <FullLoader />}
    </div>
  );
};
export default SignTemplate01;
